import React from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { colors, fontSize } from "style";
import Card from "components/Card";
import { UserFullName, UserLocation } from "components/User";
import { Row } from "components/Containers";
import Tags, { TAG_SIZE } from "components/Tags";
import { InlineProgressBar } from "components/Progress";
import Score from "components/Score";
import { Tag } from "components/Tags";
import { APPLICANT_TYPES, MISSION_ROLE_STATES } from "constants/index";
import { useAuth } from "hooks";
import { convertToPercent } from "utils";

/**
 * ApplicantBoardCard
 *
 * @param {Object} applicant
 * @param {Function} onView
 * @param {Boolean} isOverlay
 */
const ApplicantBoardCard = ({ applicant, onView, isOverlay, ...props }) => {
  const { canMoveApplicants } = useAuth();
  const isAppliedColumn = applicant.state === APPLICANT_TYPES.pending;
  const isRoleClosed = applicant?.missionRole?.state === MISSION_ROLE_STATES.closed;
  const isDragDisabled = isOverlay || isAppliedColumn || isRoleClosed || !canMoveApplicants;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: applicant.id,
    data: {
      applicant,
      fromState: applicant.state,
    },
    disabled: isDragDisabled,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
  };

  const profile = applicant.profile || {};
  const disciplines = profile.disciplines || applicant.profileData?.disciplines || [];
  const disciplineTags = disciplines
    .map((discipline) => ({ name: discipline.name || discipline }))
    .filter(({ name }) => !!name);

  return (
    <div
      ref={setNodeRef}
      style={style}
      css={styles.wrapper(isDragging, isOverlay, isAppliedColumn, isRoleClosed)}
      onClick={() => !isDragging && !isOverlay && onView(applicant)}
      {...listeners}
      {...attributes}
    >
      <Card css={styles.card} {...props}>
        <ApplicantCardIdentity profile={profile} {...props} />
        {isAppliedColumn ? (
          <AppliedCardContent applicant={applicant} disciplineTags={disciplineTags} {...props} />
        ) : (
          <PipelineCardContent applicant={applicant} disciplineTags={disciplineTags} {...props} />
        )}
      </Card>
    </div>
  );
};

const ApplicantCardIdentity = ({ profile, ...props }) => (
  <div css={styles.identity_row}>
    <UserFullName
      data={profile}
      withPhoto
      disableLink
      photoProps={{ width: "4rem", height: "4rem", borderRadius: "100%" }}
      {...props}
    >
      <div css={styles.location_row}>
        <UserLocation data={profile} hideState hideCity hideEmpty {...props} />
      </div>
    </UserFullName>
  </div>
);

const AppliedCardContent = ({ applicant, disciplineTags, ...props }) => (
  <>
    {!!disciplineTags.length && (
      <div css={styles.disciplines_row}>
        <Tags
          data={disciplineTags}
          size={TAG_SIZE.small}
          color={colors.purpleRainBase}
          background={colors.purpleRainLight5}
          limit={1}
          hideOverflowCount
          {...props}
        />
      </div>
    )}

    <AppliedChecklistProgress applicant={applicant} />
  </>
);

const PipelineCardContent = ({ applicant, disciplineTags, ...props }) => (
  <>
    {!!disciplineTags.length && (
      <div css={styles.disciplines_row}>
        <Tags
          data={disciplineTags}
          size={TAG_SIZE.small}
          color={colors.purpleRainBase}
          background={colors.purpleRainLight5}
          limit={2}
          {...props}
        />
      </div>
    )}

    <Row css={styles.metrics_row} {...props}>
      {applicant.noticePeriod && (
        <Tag size={TAG_SIZE.small} color={colors.grayAnatomyLight5} background={colors.grayAnatomyBase}>
          <FormattedMessage id={`MissionRoleApplicant.NoticePeriod.${applicant.noticePeriod}`} />
        </Tag>
      )}
      {applicant.matchScore != null && (
        <Score fontSize={fontSize.xxsmall} score={Number(applicant.matchScore) * 100} {...props}>
          {convertToPercent(applicant.matchScore)}
        </Score>
      )}
    </Row>
  </>
);

const AppliedChecklistProgress = ({ applicant, ...props }) => {
  const checklist = applicant.applicationChecklist || [];

  if (!checklist.length) return null;

  const completedCount = checklist.filter((step) => step.fulfilled).length;
  const totalCount = checklist.length;

  return (
    <div css={styles.progress_container}>
      <InlineProgressBar completed={completedCount} total={totalCount} {...props} />
    </div>
  );
};

const styles = {
  wrapper: (isDragging, isOverlay, isAppliedColumn, isRoleClosed) => css`
    opacity: ${isDragging && !isOverlay ? 0.35 : 1};
    cursor: ${isOverlay ? "grabbing" : isAppliedColumn || isRoleClosed ? "pointer" : "grab"};
    touch-action: none;
  `,
  card: css`
    padding: 1rem;
    margin-bottom: 0.75rem;
    border: 1px solid ${colors.grayAnatomyLight3};
    border-radius: 0.75rem;
    box-shadow: 0 4px 12px rgba(38, 34, 107, 0.08);
    cursor: inherit;
  `,
  identity_row: css`
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
  `,
  avatar_column: css`
    flex-shrink: 0;
  `,
  identity_content: css`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  `,
  location_row: css`
    font-size: ${fontSize.xxsmall};
    color: ${colors.grayAnatomyBase};
    margin-top: 0.5rem;
  `,
  disciplines_row: css`
    margin-top: 0.75rem;
  `,
  metrics_row: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    flex-wrap: wrap;
  `,
  progress_container: css`
    margin-top: 0.75rem;
  `,
};

ApplicantBoardCard.propTypes = {
  applicant: PropTypes.object.isRequired,
  onView: PropTypes.func.isRequired,
  isOverlay: PropTypes.bool,
};

ApplicantBoardCard.defaultProps = {
  isOverlay: false,
};

ApplicantCardIdentity.propTypes = {
  profile: PropTypes.object.isRequired,
};

AppliedChecklistProgress.propTypes = {
  applicant: PropTypes.object.isRequired,
};

AppliedCardContent.propTypes = {
  applicant: PropTypes.object.isRequired,
  disciplineTags: PropTypes.array.isRequired,
};

PipelineCardContent.propTypes = {
  applicant: PropTypes.object.isRequired,
  disciplineTags: PropTypes.array.isRequired,
};

export default ApplicantBoardCard;
